import { Injectable, computed, inject, signal } from '@angular/core';
import { RealtimeService } from 'src/app/services/realtime.service';
import { utils } from 'src/app/shared';
import { AiRun, AiRunRealtimeEvent } from '../../core/models/ai.model';
import { AiPipelineService, AiRunService } from '../../core/services/ai-api.service';
import { canReadAi } from '../../core/utils/ai-access.util';

/** Quanto tempo o "concluído" fica no rodapé. */
const FLASH_MS = 5000;
/** Execução ativa mais velha que isto é considerada perdida e não gira para sempre. */
const STALE_MS = 2 * 60 * 60 * 1000;

export interface AiActivityRun {
  id: string;
  pipelineId: string | null;
  pipelineName: string | null;
  status: 'pending' | 'running';
  /** Etapa em andamento, quando o aviso trouxe. */
  step: string | null;
}

export interface AiActivityFlash {
  kind: 'completed' | 'failed';
  pipelineName: string | null;
}

/**
 * Execuções de IA que o PRÓPRIO usuário disparou e ainda rodam, para o indicador do rodapé. Alimenta
 * por `ia-runs-{grupo}` (o aviso leva `requested_by`) e, ao iniciar, busca as ativas dele para
 * sobreviver a um F5. Só inscreve quem tem leitura em "Pipelines de IA" (o canal exige).
 */
@Injectable({ providedIn: 'root' })
export class AiRunActivityService {
  private readonly realtime = inject(RealtimeService);
  private readonly runs = inject(AiRunService);
  private readonly pipelines = inject(AiPipelineService);

  private readonly active = signal<ReadonlyMap<string, AiActivityRun>>(new Map());
  private readonly pipelineNames = signal<ReadonlyMap<string, string>>(new Map());
  private namesRequested = false;
  private flashTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;

  /** Execuções ativas, com o nome da pipeline resolvido assim que o cadastro carrega. */
  readonly running = computed(() =>
    [...this.active().values()].map(run => ({
      ...run,
      pipelineName: run.pipelineName ?? this.pipelineNames().get(run.pipelineId ?? '') ?? null,
    }))
  );
  readonly runningCount = computed(() => this.active().size);
  /** Resultado da última execução que terminou, por alguns segundos. */
  readonly flash = signal<AiActivityFlash | null>(null);

  /** Idempotente: o rodapé existe em dois layouts, só um começa a escutar. */
  start(): void {
    if (this.started || !canReadAi(true)) return;
    this.started = true;

    this.realtime.subscribeToAiRuns().subscribe((event: AiRunRealtimeEvent) => this.onEvent(event));
    this.recoverActive();
  }

  private me(): string {
    return String(utils.getUser()?.id ?? '').toLowerCase();
  }

  private onEvent(event: AiRunRealtimeEvent): void {
    const run = event?.run;
    const owner = String(run?.requested_by ?? '').toLowerCase();
    if (!run?.id || !owner || owner !== this.me()) return;

    if (run.status === 'pending' || run.status === 'running') {
      const known = this.active().get(run.id);
      this.track({
        id: run.id,
        pipelineId: run.pipeline_id ?? known?.pipelineId ?? null,
        pipelineName: known?.pipelineName ?? null,
        status: run.status,
        step: event.step?.step_name ?? known?.step ?? null,
      });
      return;
    }

    const finished = this.running().find(item => item.id === run.id);
    this.untrack(run.id);
    if (!finished) return;

    if (run.status === 'completed' || run.status === 'failed') {
      this.showFlash({ kind: run.status, pipelineName: finished.pipelineName });
    }
  }

  private async recoverActive(): Promise<void> {
    try {
      const page = await this.runs.list({
        requested_by: this.me(),
        status: ['pending', 'running'],
        size: 20,
      });
      const limit = Date.now() - STALE_MS;
      page.data
        .filter(run => new Date(run.created_at ?? 0).getTime() >= limit)
        .forEach(run => this.track(this.fromRun(run)));
    } catch {
      // Sem a lista, o indicador só passa a valer para as execuções iniciadas daqui em diante.
    }
  }

  private fromRun(run: AiRun): AiActivityRun {
    return {
      id: run.id,
      pipelineId: run.pipeline_id ?? run.pipeline?.id ?? null,
      pipelineName: run.pipeline?.name ?? null,
      status: run.status === 'running' ? 'running' : 'pending',
      step: null,
    };
  }

  private track(run: AiActivityRun): void {
    this.active.update(map => new Map(map).set(run.id, run));
    if (!run.pipelineName) this.loadPipelineNames();
  }

  private untrack(id: string): void {
    this.active.update(map => {
      const next = new Map(map);
      next.delete(id);
      return next;
    });
  }

  /** O aviso em tempo real só traz o id da pipeline: o nome vem do cadastro, uma vez. */
  private async loadPipelineNames(): Promise<void> {
    if (this.namesRequested) return;
    this.namesRequested = true;
    try {
      const list = await this.pipelines.options({ enabled: null });
      this.pipelineNames.set(new Map(list.map(pipeline => [pipeline.id, pipeline.name])));
    } catch {
      this.namesRequested = false;
    }
  }

  private showFlash(flash: AiActivityFlash): void {
    if (this.flashTimer) clearTimeout(this.flashTimer);
    this.flash.set(flash);
    this.flashTimer = setTimeout(() => this.flash.set(null), FLASH_MS);
  }
}
